import { wallIntersectsSweep, wallIntersectsCylinder, type PaintedWallGeometry, type WallPoint } from './painted-wall-geometry';
import projectileCatalog from '../../data/painted_wall_projectiles.json';
import {paintedWallPhaseVolumes, type PaintedWallSectionState} from './painted-wall-section-state';

export type WallProjectileKind = 'ordinary' | 'massive' | 'spell' | 'none';
/** Generic loose objects use their size; unsized inventory is Tiny in the throw contract. */
export function paintedWallThrownObjectKind(sizeId: string): WallProjectileKind {
  return ['', '13456FEC-A2EE-DB46-5829-0EB2F627E1B7', 'AAC1F466-7A8B-C593-1ADC-FC402B8909D7'].includes(sizeId.toUpperCase()) ? 'ordinary' : 'none';
}
/** Catalog identities distinguish giant/siege missiles; ordinary spell rays are not arrows. */
export function paintedWallProjectileKind(rollType: string, ...itemIds: string[]): WallProjectileKind {
  if (itemIds.some(id => Object.prototype.hasOwnProperty.call(projectileCatalog.massive, id.toUpperCase()))) return 'massive';
  if (rollType.toUpperCase() === '3F85CA97-F9B2-A83A-FDE8-E523FD99346C') return 'ordinary';
  if (rollType.toUpperCase() === 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C') return 'spell';
  return 'none';
}

/** Explicit catalog contracts, not name matching or a blanket line-spell conversion. */
export const RIFT_WALL_ID = '30DF4F8A-BA72-91D3-74F6-A26FA3A54B9B';
export const WIND_WALL_ID = 'B9032A2F-B68E-0D7E-A234-90CAD8959892';
export const ICE_WALL_ID = '98E982F2-46EF-1B81-87D2-6B0E8AFDE922';
export const WATER_WALL_ID = '3375068E-F4FB-C567-C7D4-D2D4B861CD10';
/** Supply surviving liquid geometry. The Fire scale is for that damage component only.
 * A single cast's retraced spans or multiple crossings never compound its modifier.
 */
export function paintedWallWaterPathEffects(abilityId: string, geometry: PaintedWallGeometry,
  points: readonly WallPoint[], projectile: WallProjectileKind = 'none'):
  { weaponDisadvantage: boolean; fireDamageScale: number } {
  if (abilityId.toUpperCase() === WATER_WALL_ID) {
    for (let i=1; i<points.length; i++) {
      if (wallIntersectsSweep(geometry,points[i-1]!,points[i]!,0,0)) {
        return { weaponDisadvantage: projectile === 'ordinary' || projectile === 'massive', fireDamageScale: .5 };
      }
    }
  }
  return { weaponDisadvantage: false, fireDamageScale: 1 };
}
export const BLADE_BARRIER_ID = 'C88CB529-C244-07ED-92F4-260FCAC62992';
export const PRISMATIC_WALL_ID = 'FC95B5FB-B47D-7848-F956-06663487666C';
/** See-through, but a line crossing it is behind HEAVY cover (owner 2026-09-22).
 * Twin of ft_painted_wall_grants_heavy_cover in painted_wall_rules.php. */
export function paintedWallGrantsHeavyCover(abilityId: string, geometry: PaintedWallGeometry, from: WallPoint, to: WallPoint): boolean {
  return abilityId.toUpperCase() === BLADE_BARRIER_ID && wallIntersectsSweep(geometry, from, to, 0, 0);
}
export interface WallMovementProfile { sizeId: string; gaseous: boolean }
export const WALL_GASEOUS_FORM_ID = 'C8DAE8AB-A29E-49E4-B4C6-5FDFAE068DBE';
export const WALL_GASEOUS_STATUS_IDS = [WALL_GASEOUS_FORM_ID, '51A206FC-6149-F902-299C-611AF1185643'];
/** The Telekinetic menta Force Wall: a solid painted wall no body passes (twin of ft_painted_wall_restricts_mover, R-142). */
export const FORCE_WALL_ID = '2999073F-2DDA-E9D5-64F2-07B7F1E3105F';
export function paintedWallRestrictsMover(abilityId: string, profile: WallMovementProfile, flying: boolean): boolean {
  if([ICE_WALL_ID,WATER_WALL_ID,FORCE_WALL_ID].includes(abilityId.toUpperCase()))return true;
  return abilityId.toUpperCase() === WIND_WALL_ID && (profile.gaseous || (flying
    && ['13456FEC-A2EE-DB46-5829-0EB2F627E1B7', 'AAC1F466-7A8B-C593-1ADC-FC402B8909D7'].includes(profile.sizeId.toUpperCase())));
}
/** A wall cast over a creature permits departure; an outside body cannot enter or tunnel through. */
export function paintedWallBlocksMovement(geometry: PaintedWallGeometry, from: WallPoint, to: WallPoint,
  radius: number, height: number): boolean {
  return !wallIntersectsCylinder(geometry, from, radius, height)
    && wallIntersectsSweep(geometry, from, to, radius, height);
}
export function paintedWallBlocksRangedPath(abilityId: string, geometry: PaintedWallGeometry,
  points: readonly WallPoint[], projectile: WallProjectileKind = 'none',
  sections: PaintedWallSectionState | null = null, targetInventoryId = ''): boolean {
  const id=abilityId.toUpperCase();
  if(id===ICE_WALL_ID||id===WATER_WALL_ID) {
    // Exclude only the attacked inventory interval, never its whole cast. Another
    // intact span (including a retraced span) can still stand in front of it.
    const state=sections&&targetInventoryId ? {...sections,entries:sections.entries.map(entry=>
      entry.inventoryId.toUpperCase()===targetInventoryId.toUpperCase()?{...entry,phase:'breached' as const}:entry)} : sections;
    const volumes=paintedWallPhaseVolumes(geometry,state)[id===WATER_WALL_ID?'frozen':'solid'];
    return volumes.some(wall=>points.slice(1).some((end,index)=>wallIntersectsSweep(wall,points[index]!,end,0,0)));
  }
  // Prismatic (owner 2026-09-23): intact Red stops nonmagical missiles, intact Indigo stops spells cast through.
  const prismatic = id === PRISMATIC_WALL_ID && (projectile === 'ordinary' || projectile === 'massive' || projectile === 'spell');
  if (!prismatic && abilityId.toUpperCase() !== RIFT_WALL_ID
    && !(abilityId.toUpperCase() === WIND_WALL_ID && projectile === 'ordinary')) return false;
  for (let i = 1; i < points.length; i++) {
    if (wallIntersectsSweep(geometry, points[i - 1]!, points[i]!, 0, 0)) return true;
  }
  return false;
}

/** Earliest contact on one segment, for a preview that stops at the actual wall face. */
export function paintedWallSegmentHit(abilityId: string, geometry: PaintedWallGeometry,
  from: WallPoint, to: WallPoint, projectile: WallProjectileKind,
  sections: PaintedWallSectionState | null = null): number | null {
  if (!paintedWallBlocksRangedPath(abilityId, geometry, [from, to], projectile, sections)) return null;
  let low = 0, high = 1;
  for (let i = 0; i < 32; i++) {
    const t = (low + high) / 2;
    const end = { x: from.x + (to.x-from.x)*t, y: from.y + (to.y-from.y)*t, z: from.z + (to.z-from.z)*t };
    if (paintedWallBlocksRangedPath(abilityId, geometry, [from, end], projectile, sections)) high = t; else low = t;
  }
  return high;
}
